import React, { memo, useEffect, useReducer } from 'react';
import { View, Clipboard } from 'react-native';
import { createUseStyle } from '@redlink/assets/styles';
import {
  Space,
  WebScrollView,
  DragableFlatList,
} from '@redlink/components/atoms';
import {
  PreviewContainer,
  useDialog,
  LinkTemplates,
  useAlert,
} from '@redlink/components/molecules';
import { LinkItem } from '@redlink/components/muscles';
import { ListContainer } from '@redlink/components/organisms';
import {
  PreviewLinkPage,
  LinkEditPage,
  LinkTemplatesEditPage,
  PreviewBasicProfilePage,
} from '@redlink/components/pages';
import { useLocale } from '@redlink/assets/locale';
import {
  useQuery,
  useMutation,
  getPageLinksQuery,
  getUserProfileQuery,
  getPageProfileQuery,
  setLinkVisibilityQuery,
  getPagePreviewLinkQuery,
  removeLinkQuery,
  reorderLinkQuery,
} from '@redlink/services';
import {
  freshCacheForVisibility,
  freshCacheForRemoveItem,
  freshCacheForReoderItem,
} from './cache';
import { initialState, reducer, types } from './store';

export const LinkPage = memo(() => {
  const alert = useAlert();
  const { styles } = useStyle();
  const { dictionary } = useLocale();
  const { showConfirmDialog } = useDialog();

  const {
    loading: getPageLinksIsBusy,
    data: { Links },
  } = useQuery(getPageLinksQuery);

  useEffect(() => {
    dispatch({ type: types.SET_LINKS, payload: Links });
    return () => {};
  }, [Links]);

  const [
    {
      links,
      newLinkIsVisible,
      currentLink,
      selectTemplatesIsVisible,
      selectedTemplateIndex,
    },
    dispatch,
  ] = useReducer(reducer, initialState(Links));

  const {
    data: { PageProfile: { pageName } = {} },
  } = useQuery(getPageProfileQuery);

  const { refetch: refetchPreviewLinks } = useQuery(getPagePreviewLinkQuery, {
    variables: { pageName },
  });

  const [setLinkVisibility] = useMutation(setLinkVisibilityQuery, {
    done: () => refetchPreviewLinks({ pageName }),
    update: freshCacheForVisibility(),
  });

  const [removeLink] = useMutation(removeLinkQuery, {
    done: () => {},
    update: freshCacheForRemoveItem({ pageName }),
  });

  const [reorderLink] = useMutation(reorderLinkQuery, {
    done: () => refetchPreviewLinks({ pageName }),
  });

  const onDragEnd = ({ destination, source, draggableId: id }) => {
    if (!destination || source.index === destination.index) {
      return;
    }

    reorderLink({ id, newOrder: destination.index + 1 });

    freshCacheForReoderItem({
      oldIndex: source.index,
      newIndex: destination.index,
    });
  };

  const renderItem = ({ item, index, dragHandle }) => (
    <LinkItem
      {...{ item, dragHandle }}
      onEditPressed={() => {
        dispatch({
          type: types.SET_MULTI,
          payload: {
            currentLink: item,
            newLinkIsVisible: true,
          },
        });
      }}
      onCopyPressed={() => {
        Clipboard.setString(item.shortLinkUrl);
        alert.show(dictionary.copiedInClipboard, { type: 'info' });
      }}
      onChangeAvailability={(value) => {
        setLinkVisibility({ id: item.id, isVisible: value });
      }}
      onDelete={() => {
        showConfirmDialog({
          message: dictionary.deleteConfirmMessage,
          onConfirm: () => removeLink({ id: item.id }),
        });
      }}
    />
  );

  return (
    <View style={styles.container}>
      <WebScrollView>
        <LinkTemplates
          onAddPress={() => {
            dispatch({
              type: types.SET_MULTI,
              payload: {
                currentLink: null,
                newLinkIsVisible: true,
              },
            });
          }}
          title={dictionary.templates}
          onSelect={(item, index) => {
            dispatch({
              type: types.SET_MULTI,
              payload: {
                selectTemplatesIsVisible: true,
                selectedTemplateIndex: index,
              },
            });
          }}
        />
        <Space size={25} />
        <ListContainer
          isFetching={getPageLinksIsBusy}
          placeholder={{
            imageSource: require('@redlink/assets/images/landing_image_2.png'),
            description: dictionary.addYourFirstLink,
          }}
          data={Links}
          title={dictionary.links}
        >
          <DragableFlatList
            data={links}
            ItemSeparatorComponent={() => <Space />}
            {...{ renderItem, onDragEnd }}
          />
        </ListContainer>
      </WebScrollView>
      <PreviewContainer>
        <PreviewLinkPage {...{ pageName }} />
      </PreviewContainer>
      <LinkEditPage
        isVisible={newLinkIsVisible}
        {...{ pageName }}
        {...(currentLink && { data: currentLink })}
        onDismiss={() =>
          dispatch({
            type: types.SET_NEW_LINK_IS_VISIBLE,
            payload: false,
          })
        }
      />
      <LinkTemplatesEditPage
        isVisible={selectTemplatesIsVisible}
        {...{ pageName }}
        initialSelectedItemIndex={selectedTemplateIndex}
        onDismiss={() => {
          dispatch({
            type: types.SET_SELECT_TEMPLATES_IS_VISIBLE,
            payload: false,
          });
        }}
      />
    </View>
  );
});

const useStyle = createUseStyle(({ colors }) => ({
  container: {
    flex: 1,
    flexDirection: 'row',
  },
}));
